import { Button, CircularProgress, Grid } from "@material-ui/core";
import { Launch, Print } from "@material-ui/icons";
import moment from "moment";
import { useContext, useState } from "react";
import { utils, writeFile } from "xlsx";
import { QueryParamsContext } from "../../../../../context/QueryParamsContext";
import { printCashFlowPDF } from "../../../../../repositories/qerpapi/erp/relatorio/FluxoDeCaixaRepository";
import { notify } from "../../../../../utils/notify";
import {
  groupOptions,
  movimentsTypes,
  periods,
  reportTypes,
} from "../../../constants";
import { useFluxoDeCaixaService } from "../../contexts";
import OrientationMenuButton from "../OrientationMenuButton";

function ExportButton({ data, filename }) {
  const { columns, filters } = useFluxoDeCaixaService();
  const isDisabled = !data?.length;

  const exportToXLSX = () => {
    const rows = [];
    const headersIds = {};

    const header = [
      "",
      ...columns.map((colum) => {
        headersIds[colum.id] = colum.id;
        return colum.label;
      }),
    ];

    rows.push(header);

    data.forEach((group) => {
      const formatedPeriodGroup = {
        day: moment(group?.period?.startDate, "YYYY-MM-DD").format(
          "DD/MM/YYYY"
        ),
        week: `${moment(group?.period?.startDate, "YYYY-MM-DD").format(
          "DD/MM/YYYY"
        )} - ${moment(group?.period?.endDate, "YYYY-MM-DD").format(
          "DD/MM/YYYY"
        )}`,
        month: moment(group?.period?.startDate, "YYYY-MM-DD").format("MM/YYYY"),
      };
      if (filters.reportType === reportTypes.Detalhado)
        rows.push([formatedPeriodGroup[group?.period?.type ?? "day"]]);

      group.accounts?.forEach((item) => {
        const row = [
          "",
          ...columns.map((column) =>
            column?.format ? column.format(item[column.id]) : item[column.id]
          ),
        ];
        rows.push(row);
      });

      const totalsLabel =
        filters.reportType === reportTypes.Consolidado
          ? formatedPeriodGroup[group?.period?.type ?? "day"]
          : "Totais: ";

      rows.push([
        totalsLabel,
        ...columns.map((column) =>
          ["debit", "credit", "balance"].includes(column.id)
            ? group.total[column.id]
            : ""
        ),
      ]);
    });

    const sheet = utils.aoa_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, sheet, "Sheet1");
    writeFile(workbook, `${filename}.xlsx`);
  };

  return (
    <Button
      size="small"
      variant="contained"
      endIcon={<Launch />}
      style={
        isDisabled
          ? null
          : {
              backgroundColor: "#1d6f42",
              color: "#f4f4f4",
            }
      }
      onClick={exportToXLSX}
      disabled={isDisabled}
    >
      XLSX
    </Button>
  );
}

export const ReportActions = () => {
  const {
    cachedPeriodResult: period,
    filters,
    cachedMovimentData: cashFlow,
    cachedTotalsData: totals,
    cachedPreviousBalance: previousBalance,
    financeCategoriesFilter,
    orientation,
  } = useFluxoDeCaixaService();
  const { queryParams } = useContext(QueryParamsContext);
  const [printLoading, setPrintLoading] = useState(false);
  return (
    <Grid item container direction="row" spacing={1}>
      <Grid item xs></Grid>
      <Grid item>
        <ExportButton data={cashFlow} filename={`fluxo-caixa-${Date.now()}`} />
      </Grid>

      <Grid item>
        <Button
          size="small"
          variant="contained"
          startIcon={printLoading ? <CircularProgress size={16} /> : <Print />}
          color="primary"
          disabled={!cashFlow.length || printLoading}
          onClick={async () => {
            setPrintLoading(true);
            const filtersDescriptions = {
              period: {
                label: "Período",
                formatedValue: (searchPeriod) => {
                  const objEntries = Object.entries(periods);
                  const [key] = objEntries.find(
                    ([_, value]) => value === searchPeriod
                  );
                  return key;
                },
              },
              startDate: {
                label: "Data inicial",
                formatedValue: (date) =>
                  moment(date, "YYYY-MM-DD").format("DD/MM/YYYY"),
              },
              endDate: {
                label: "Data final",
                formatedValue: (date) =>
                  moment(date, "YYYY-MM-DD").format("DD/MM/YYYY"),
              },
              moviment: {
                label: "Movimentação",
                formatedValue: (moviment) => {
                  const objEntries = Object.entries(movimentsTypes);
                  const [key] = objEntries.find(
                    ([_, value]) => value === moviment
                  );
                  return key;
                },
              },
              multicompany: {
                label: "Empresa",
                formatedValue: () => "Empresa",
              },
              reportType: {
                label: "Tipo",
                formatedValue: (type) => {
                  const objEntries = Object.entries(reportTypes);
                  const [key] = objEntries.find(([_, value]) => value === type);
                  return key;
                },
              },
              group: {
                label: "Agrupamento",
                formatedValue: (group) => {
                  const objEntries = Object.entries(groupOptions);
                  const [key] = objEntries.find(
                    ([_, value]) => value === group
                  );
                  return key;
                },
              },
              showPreviousBalance: {
                label: "Exibir Saldo Anterior",
                formatedValue: (value) => (value ? "Sim" : "Não"),
              },
              showNotPayedAccounts: {
                label: "Exibir Contas não pagas",
                formatedValue: (value) => (value ? "Sim" : "Não"),
              },
              showNotReceivedTitles: {
                label: "Exibir Títulos não recebidos",
                formatedValue: (value) => (value ? "Sim" : "Não"),
              },
              useFinanceCategories: {
                label: "Exibir categorias do financeiro",
                formatedValue: (value) => (value ? "Sim" : "Não"),
              },
              financeCategories: {
                label: "Categorias do financeiro",
                formatedValue: (value) => {
                  if (!filters.useFinanceCategories) {
                    return "Exibir categorias desativado";
                  }
                  const categoryIds = value.split(",");
                  const financeCategories = financeCategoriesFilter.filtered
                    .filter((category) =>
                      categoryIds.includes(`${category.id}`)
                    )
                    .map((category) => category.desc);

                  if (!financeCategories.length) {
                    return "Exibindo todas as categorias";
                  }

                  return financeCategories.join(",");
                },
              },
            };

            const pdfBody = {
              title: "Fluxo de caixa",
              type:
                filters.reportType === "detailed" ? "DETALHADO" : "CONSOLIDADO",
              orientation: orientation,
              group:
                filters.group === "day"
                  ? "DIA"
                  : filters.group === "week"
                  ? "SEMANA"
                  : "MES",
              companyName: queryParams?.nomeempresa,
              companyFantasy: queryParams?.apelido,
              headerLogo: queryParams?.imagebase64,
              footerLogo: queryParams?.imagebase64,
              userLogged: queryParams?.usuario_nome,
              filter: Object.entries(filters).map(([key, value]) => ({
                field: filtersDescriptions[key].label,
                value: filtersDescriptions[key].formatedValue(value),
              })),
              period,
              useCategoryGroup: filters.useFinanceCategories,
              useInitialBalance: filters.showPreviousBalance,
              initialBalanceTotal: previousBalance?.totals ?? 0,
              initialBalanceGroup: previousBalance?.companies?.map(
                (company) => ({
                  company: company.fantasy,
                  initialBalance: company.agencies.map((agency) => ({
                    bank: agency.description,
                    balance: agency.balance,
                  })),
                })
              ),
              generalAccountReceivableTotal: totals?.CR ?? 0,
              generalAccountNotReceivableTotal: totals?.openedCR ?? 0,
              generalAccountPayableTotal: totals?.CP ?? 0,
              generalAccountNotPayableTotal: totals?.openedCP ?? 0,
              generalBalance: totals?.balance ?? 0,
              cashFlowGroup: cashFlow?.map((moviment) => {
                return {
                  groupTitle:
                    moviment.period.type === "day"
                      ? `Dia: ${moment(
                          moviment.period.startDate,
                          "YYYY-MM-DD"
                        ).format("DD/MM/YYYY")}`
                      : moviment.period.type === "week"
                      ? `Semana: ${moment(
                          moviment.period.startDate,
                          "YYYY-MM-DD"
                        ).format("DD/MM/YYYY")} - ${moment(
                          moviment.period.endDate,
                          "YYYY-MM-DD"
                        ).format("DD/MM/YYYY")}`
                      : `Mês: ${moment(
                          moviment.period.startDate,
                          "YYYY-MM-DD"
                        ).format("MM/YYYY")}`,
                  cashFlow: [
                    {
                      groupCreditTotal: moviment.total.credit,
                      groupDebitTotal: moviment.total.debit,
                      groupBalanceTotal: moviment.total.balance,

                      accounts: moviment?.accounts?.map((account) => ({
                        company: account.company,
                        category: account?.businessPlan || "",
                        description: account.description,
                        document: account.document,
                        date: account.date,
                        jurisdiction: account.jurisdiction,
                        status: account.situation,
                        paymentType: account.doccumentType,
                        debit: account.debit,
                        credit: account.credit,
                        balance: account.balance,
                      })),
                    },
                  ],
                };
              }),
            };
            const res = await printCashFlowPDF(pdfBody);
            setPrintLoading(false);
            if (res?.status !== 200) {
              return notify("Falha ao imprimir relatório", true, "error");
            }

            const base64PDF = res?.data?.fileBase64;
            const binaryString = atob(base64PDF);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url);
          }}
        >
          {printLoading ? "Gerando relatório..." : "Imprimir"}
        </Button>
      </Grid>
      <Grid item>
        <OrientationMenuButton label={"Orientação"} />
      </Grid>
    </Grid>
  );
};
